import React, { useState, useRef, useCallback } from 'react';
import { Input, Button, message, Tabs, Card, Avatar, Typography, Spin, Empty } from 'antd';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  ExpandOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  LinkOutlined,
  PictureOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FireOutlined
} from '@ant-design/icons';
import styles from './YouTubePlayerPageMobile.module.css';

const { Text } = Typography;
const { TabPane } = Tabs;

interface VideoResult {
  videoId: string;
  title: string;
  author: string;
  authorId: string;
  videoThumbnails: Array<{
    quality: string;
    url: string;
    width: number;
    height: number;
  }>;
  description: string;
  viewCount: number;
  published: number;
  publishedText: string;
  lengthSeconds: number;
  lengthText: string;
}

const YouTubePlayerPageMobile: React.FC = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [isPiPSupported, setIsPiPSupported] = useState(false);
  const [status, setStatus] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);
  const [activeTab, setActiveTab] = useState('url');
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VideoResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<VideoResult | null>(null);
  
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Kiểm tra hỗ trợ Picture-in-Picture khi component mount
  React.useEffect(() => {
    const checkPiPSupport = () => {
      // Luôn enable PiP vì chúng ta sử dụng popup approach
      // Popup window được hỗ trợ trên hầu hết browsers
      setIsPiPSupported(true);
    };
    
    checkPiPSupport();
  }, []);

  // Function tìm kiếm YouTube sử dụng approach mới
  const searchYouTube = useCallback(async (query: string): Promise<VideoResult[]> => {
    try {
      // Approach 1: Thử dùng JSONP để bypass CORS
      const searchWithJsonp = (instanceUrl: string): Promise<VideoResult[]> => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          const callbackName = `jsonp_callback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          // Cleanup function
          const cleanup = () => {
            document.head.removeChild(script);
            delete (window as any)[callbackName];
          };

          // Setup callback
          (window as any)[callbackName] = (data: any) => {
            cleanup();
            if (Array.isArray(data) && data.length > 0) {
              resolve(data.slice(0, 20));
            } else {
              resolve([]);
            }
          };

          // Setup error handling
          script.onerror = () => {
            cleanup();
            reject(new Error('JSONP request failed'));
          };

          // Timeout after 8 seconds
          setTimeout(() => {
            cleanup();
            reject(new Error('JSONP request timeout'));
          }, 8000);

          // Make request
          script.src = `${instanceUrl}/api/v1/search?q=${encodeURIComponent(query)}&type=video&sort_by=relevance&duration=any&features=any&callback=${callbackName}`;
          document.head.appendChild(script);
        });
      };

      // List of instances that potentially support JSONP or have CORS enabled
      const instances = [
        'https://vid.puffyan.us',
        'https://invidious.protokolla.fi',
        'https://invidious.privacydev.net',
        'https://inv.bp.projectsegfau.lt'
      ];

      // Fallback: Tạo mock data dựa trên search query
      const createMockResults = (searchQuery: string): VideoResult[] => {
        const musicGenres = ['Pop', 'Rock', 'Hip Hop', 'Jazz', 'Classical', 'Electronic', 'Folk', 'R&B'];
        const artists = ['Various Artists', 'Top Hits', 'Music Mix', 'Best Songs', 'Compilation'];
        
        const mockResults: VideoResult[] = [];
        const baseId = Date.now().toString();
        
        for (let i = 0; i < 10; i++) {
          const genre = musicGenres[i % musicGenres.length];
          const artist = artists[i % artists.length];
          
          mockResults.push({
            videoId: `${baseId}_${i}`,
            title: `${searchQuery} - ${genre} Mix ${i + 1}`,
            author: artist,
            authorId: `channel_${i}`,
            videoThumbnails: [
              {
                quality: 'medium',
                url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRkY4OUExIi8+Cjx0ZXh0IHg9IjE2MCIgeT0iOTAiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNiI+4pmrPC90ZXh0Pgo8L3N2Zz4K',
                width: 320,
                height: 180
              }
            ],
            description: `Tìm kiếm: ${searchQuery} - ${genre} music compilation`,
            viewCount: Math.floor(Math.random() * 1000000) + 10000,
            published: Date.now() - (Math.random() * 365 * 24 * 60 * 60 * 1000),
            publishedText: `${Math.floor(Math.random() * 30) + 1} ngày trước`,
            lengthSeconds: Math.floor(Math.random() * 600) + 180,
            lengthText: `${Math.floor(Math.random() * 10) + 3}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`
          });
        }
        
        return mockResults;
      };

      // Approach 2: Thử direct fetch với no-cors mode (limited nhưng có thể hoạt động)
      const searchWithFetch = async (instanceUrl: string): Promise<VideoResult[]> => {
        try {
          const response = await fetch(
            `${instanceUrl}/api/v1/search?q=${encodeURIComponent(query)}&type=video&sort_by=relevance&duration=any&features=any`,
            {
              method: 'GET',
              mode: 'cors',
              headers: {
                'Accept': 'application/json',
              },
              signal: AbortSignal.timeout(6000)
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
              return data.slice(0, 20);
            }
          }
          return [];
        } catch (error) {
          console.warn(`Fetch failed for ${instanceUrl}:`, error);
          return [];
        }
      };

      // Thử các approaches theo thứ tự
      let results: VideoResult[] = [];

      // 1. Thử JSONP trước
      for (const instance of instances) {
        try {
          console.log(`Trying JSONP search with ${instance}...`);
          results = await searchWithJsonp(instance);
          if (results.length > 0) {
            console.log(`JSONP search successful with ${instance}`);
            break;
          }
        } catch (error) {
          console.warn(`JSONP failed for ${instance}:`, error);
        }
      }

      // 2. Nếu JSONP không hoạt động, thử fetch thông thường
      if (results.length === 0) {
        for (const instance of instances) {
          try {
            console.log(`Trying fetch search with ${instance}...`);
            results = await searchWithFetch(instance);
            if (results.length > 0) {
              console.log(`Fetch search successful with ${instance}`);
              break;
            }
          } catch (error) {
            console.warn(`Fetch failed for ${instance}:`, error);
          }
        }
      }

      // 3. Fallback: Sử dụng mock data với hướng dẫn tìm trên YouTube
      if (results.length === 0) {
        console.log('All API approaches failed, using mock results with YouTube redirect');
        results = createMockResults(query);
        
        // Thêm thông báo cho user
        setTimeout(() => {
          message.info({
            content: `Không thể kết nối API tìm kiếm. Hiển thị kết quả mẫu. Nhấp vào video sẽ tìm trực tiếp trên YouTube với từ khóa "${query}".`,
            duration: 8,
          });
        }, 500);
      }

      return results;
    } catch (error) {
      console.error('Lỗi tìm kiếm YouTube:', error);
      throw new Error('Không thể tìm kiếm video. Vui lòng thử lại sau.');
    }
  }, []);

  // Xử lý tìm kiếm
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      message.warning('Vui lòng nhập từ khóa tìm kiếm');
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    setStatus({type: 'info', message: 'Đang tìm kiếm video...'});

    try {
      const results = await searchYouTube(searchQuery.trim());
      
      if (results.length === 0) {
        setStatus({type: 'error', message: 'Không tìm thấy video nào. Thử từ khóa khác.'});
      } else {
        setSearchResults(results);
        setStatus({type: 'success', message: `Tìm thấy ${results.length} video`});
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Lỗi không xác định';
      setStatus({type: 'error', message: errorMessage});
      message.error(errorMessage);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, searchYouTube]);

  // Hàm chuyển đổi URL YouTube thành embed URL với các tham số chặn quảng cáo
  const convertToEmbedUrl = useCallback((url: string): string | null => {
    try {
      // Các pattern URL YouTube phổ biến
      const patterns = [
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
        /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]+)/,
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]+)/
      ];

      let videoId = null;
      
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          videoId = match[1];
          break;
        }
      }

      if (!videoId) {
        return null;
      }

      // Tạo embed URL với tham số chặn quảng cáo nâng cao
      const embedParams = new URLSearchParams({
        // Tham số cơ bản
        'autoplay': '0',          // Không tự động phát
        'controls': '1',          // Hiển thị controls
        'rel': '0',               // Không hiển thị video liên quan
        'modestbranding': '1',    // Ẩn logo YouTube
        'fs': '1',                // Cho phép fullscreen
        'playsinline': '1',       // Phát inline trên mobile
        
        // Tham số chặn quảng cáo nâng cao
        'iv_load_policy': '3',    // Ẩn video annotations
        'cc_load_policy': '0',    // Không hiển thị phụ đề mặc định
        'disablekb': '0',         // Cho phép keyboard shortcuts
        'showinfo': '0',          // Ẩn thông tin video (legacy nhưng vẫn hữu ích)
        'color': 'white',         // Màu progress bar
        'theme': 'dark',          // Dark theme
        
        // Tham số tối ưu hóa và bảo mật
        'enablejsapi': '1',       // Cho phép JavaScript API
        'origin': window.location.origin,  // Set origin cho bảo mật
        'widget_referrer': window.location.href,
        
        // Tham số experimental để giảm ads
        'ads': '0',               // Experimental: Disable ads
        'ad_frequency': '0',      // Experimental: Ad frequency
        'adformat': 'none',       // Experimental: Ad format
      });

      // Sử dụng youtube-nocookie.com để tăng privacy và giảm tracking
      return `https://www.youtube-nocookie.com/embed/${videoId}?${embedParams.toString()}`;
    } catch (error) {
      console.error('Lỗi khi chuyển đổi URL:', error);
      return null;
    }
  }, []);

  // Xử lý chọn video từ kết quả tìm kiếm
  const handleSelectVideo = useCallback((video: VideoResult) => {
    // Kiểm tra nếu đây là mock video (videoId bắt đầu với timestamp)
    const isMockVideo = video.videoId.includes('_') && video.videoId.match(/^\d+_\d+$/);
    
    if (isMockVideo) {
      // Với mock video, tìm trực tiếp trên YouTube
      const searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
      
      setStatus({type: 'info', message: 'Đang chuyển hướng đến YouTube để tìm video thực...'});
      
      // Mở YouTube search trong tab mới
      window.open(searchUrl, '_blank');
      
      message.info({
        content: `Đã mở tìm kiếm "${searchQuery}" trên YouTube. Chọn video bạn muốn và copy URL về đây để phát!`,
        duration: 8,
      });
      
      // Chuyển về tab URL để user có thể paste
      setActiveTab('url');
      
    } else {
      // Video thực từ API
      const videoUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
      const convertedUrl = convertToEmbedUrl(videoUrl);
      
      if (convertedUrl) {
        setEmbedUrl(convertedUrl);
        setCurrentVideo(video);
        setStatus({type: 'success', message: `Đang phát: ${video.title}`});
        
        // Chuyển về tab player sau khi chọn video
        setActiveTab('url');
      } else {
        message.error('Không thể phát video này');
      }
    }
  }, [convertToEmbedUrl, searchQuery]);

  // Xử lý phím Enter trong search input
  const handleSearchKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Xử lý khi người dùng nhập URL
  const handleLoadVideo = useCallback(() => {
    if (!videoUrl.trim()) {
      setStatus({type: 'error', message: 'Vui lòng nhập URL video YouTube'});
      return;
    }

    const convertedUrl = convertToEmbedUrl(videoUrl.trim());
    
    if (!convertedUrl) {
      setStatus({type: 'error', message: 'URL không hợp lệ. Vui lòng nhập URL YouTube hợp lệ.'});
      return;
    }

    setEmbedUrl(convertedUrl);
    setStatus({type: 'success', message: 'Video đã được tải thành công với tính năng chặn quảng cáo!'});
  }, [videoUrl, convertToEmbedUrl]);

  // Xử lý Picture-in-Picture - Hỗ trợ iOS và Desktop
  const handlePictureInPicture = useCallback(async () => {
    if (!embedUrl) {
      message.warning('Vui lòng tải video trước khi sử dụng Picture-in-Picture');
      return;
    }

    try {
      const videoId = embedUrl.match(/embed\/([a-zA-Z0-9_-]+)/)?.[1];
      if (!videoId) {
        message.error('Không thể lấy video ID');
        return;
      }

      // Kiểm tra nếu đang ở iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
      const isiOSDevice = isIOS || isIPadOS;

      if (isiOSDevice) {
        // iOS approach: Tạo video element và sử dụng PiP API của iOS
        try {
          // Tạo video element ẩn
          const video = document.createElement('video');
          video.style.display = 'none';
          video.controls = true;
          video.playsInline = true;
          video.muted = true; // iOS yêu cầu muted để autoplay
          
          // Sử dụng YouTube API để lấy video stream
          // Approach: Mở YouTube trong fullscreen rồi hướng dẫn user bật PiP
          const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
          
          setStatus({type: 'info', message: 'Đang chuyển hướng đến YouTube cho iOS...'});
          
          // Mở YouTube app hoặc web
          window.location.href = youtubeUrl;
          
          // Hướng dẫn user
          setTimeout(() => {
            message.info({
              content: 'Trên iOS: Phát video → Nhấn nút PiP (hình vuông nhỏ) → Video sẽ thu nhỏ ở góc màn hình!',
              duration: 10,
            });
          }, 1000);
          
        } catch (iosError) {
          console.error('iOS PiP error:', iosError);
          
          // Fallback cho iOS: Mở YouTube app
          const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
          window.open(youtubeUrl, '_blank');
          
          message.info({
            content: 'Đã mở video trên YouTube. Sử dụng nút Picture-in-Picture trong YouTube app!',
            duration: 8,
          });
        }
        
      } else {
        // Desktop/Android approach
        const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}&t=0s`;
        
        // Thử mở popup window
        const pipWindow = window.open(
          youtubeUrl,
          'youtube-pip',
          'width=480,height=360,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no'
        );

        if (pipWindow) {
          setStatus({type: 'info', message: 'Đã mở Mini Player! Sử dụng PiP native trong YouTube.'});
          pipWindow.focus();
          
          setTimeout(() => {
            message.info({
              content: 'Tip: Nhấp chuột phải vào video → "Picture in picture" để bật PiP!',
              duration: 6,
            });
          }, 1000);
        } else {
          // Popup bị chặn
          setStatus({type: 'info', message: 'Popup bị chặn. Mở YouTube trong tab mới...'});
          window.open(youtubeUrl, '_blank');
          
          message.info({
            content: 'Video đã mở trên YouTube. Sử dụng Picture-in-Picture trong YouTube!',
            duration: 8,
          });
        }
      }

    } catch (error) {
      console.error('Lỗi Picture-in-Picture:', error);
      
      // Ultimate fallback: Fullscreen
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.requestFullscreen) {
          await iframe.requestFullscreen();
          setStatus({type: 'info', message: 'Đã chuyển sang chế độ fullscreen'});
        } else {
          message.error('Không thể bật Picture-in-Picture hoặc fullscreen');
        }
      } catch (fullscreenError) {
        message.error('Tính năng Picture-in-Picture không khả dụng trên thiết bị này');
      }
    }
  }, [embedUrl]);

  // Xử lý phím Enter trong input
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLoadVideo();
    }
  }, [handleLoadVideo]);

  // Xóa video hiện tại
  const handleClearVideo = useCallback(() => {
    setEmbedUrl('');
    setVideoUrl('');
    setCurrentVideo(null);
    setStatus(null);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.youtubeHeader}>
          <div className={styles.youtubeLogo}>
            <div className={styles.logoIcon}>▶</div>
            <span>YouTube</span>
          </div>
          <div className={styles.searchContainer}>
            {currentVideo ? (
              <div className={styles.nowPlaying}>
                <FireOutlined /> {currentVideo.title}
              </div>
            ) : (
              <div className={styles.title}>YouTube Player</div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.playerSection}>
        {/* Tabs để chuyển đổi giữa URL và Search */}
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          className={styles.tabs}
          size="small"
        >
          <TabPane tab={<span><LinkOutlined /> URL</span>} key="url">
            {/* Input URL */}
            <div className={styles.urlInput}>
              <Input
                className={styles.urlInputField}
                placeholder="Nhập URL video YouTube..."
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyPress={handleKeyPress}
                prefix={<LinkOutlined />}
              />
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />}
                onClick={handleLoadVideo}
                style={{
                  backgroundColor: '#ff0000',
                  borderColor: '#ff0000',
                  borderRadius: '20px',
                  fontWeight: '500'
                }}
              >
                Phát
              </Button>
            </div>
          </TabPane>
          
          <TabPane tab={<span><SearchOutlined /> Tìm kiếm</span>} key="search">
            {/* Search Input */}
            <div className={styles.searchSection}>
              <div className={styles.searchInput}>
                <Input
                  className={styles.searchInputField}
                  placeholder="Tìm kiếm nhạc, video YouTube..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  prefix={<SearchOutlined />}
                />
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={isSearching}
                  style={{
                    backgroundColor: '#ff0000',
                    borderColor: '#ff0000',
                    borderRadius: '20px',
                    fontWeight: '500'
                  }}
                >
                  Tìm
                </Button>
              </div>
              
              {/* Search Results */}
              <div className={styles.searchResults}>
                {isSearching && (
                  <div className={styles.searchLoading}>
                    <Spin size="large" />
                    <Text>Đang tìm kiếm video...</Text>
                  </div>
                )}
                
                {!isSearching && searchResults.length === 0 && searchQuery && (
                  <Empty
                    description="Không tìm thấy video nào"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
                
                {!isSearching && searchResults.length > 0 && (
                  <div className={styles.videoList}>
                    {/* Thông báo nếu đây là mock results */}
                    {searchResults.length > 0 && searchResults[0].videoId.includes('_') && searchResults[0].videoId.match(/^\d+_\d+$/) && (
                      <div className={styles.mockResultsNotice}>
                        <SearchOutlined /> 
                        <Text type="secondary">
                          Kết quả mẫu - Nhấp vào video để tìm trên YouTube
                        </Text>
                      </div>
                    )}
                    
                    {searchResults.map((video) => {
                      const isMockVideo = video.videoId.includes('_') && video.videoId.match(/^\d+_\d+$/);
                      
                      return (
                        <Card
                          key={video.videoId}
                          className={`${styles.videoCard} ${isMockVideo ? styles.mockVideoCard : ''}`}
                          hoverable
                          onClick={() => handleSelectVideo(video)}
                          cover={
                            <div className={styles.videoCover}>
                              <img
                                src={video.videoThumbnails.find(t => t.quality === 'medium')?.url || video.videoThumbnails[0]?.url}
                                alt={video.title}
                                className={styles.videoThumbnail}
                              />
                              <div className={styles.videoDuration}>
                                {video.lengthText}
                              </div>
                              {isMockVideo && (
                                <div className={styles.mockBadge}>
                                  <SearchOutlined /> Tìm trên YouTube
                                </div>
                              )}
                            </div>
                          }
                        >
                          <Card.Meta
                            avatar={<Avatar icon={<UserOutlined />} size="small" />}
                            title={
                              <Text className={styles.videoTitle} ellipsis>
                                {video.title}
                              </Text>
                            }
                            description={
                              <div className={styles.videoMeta}>
                                <Text type="secondary" className={styles.videoAuthor}>
                                  {video.author}
                                </Text>
                                <div className={styles.videoStats}>
                                  <span>
                                    <EyeOutlined /> {new Intl.NumberFormat().format(video.viewCount)} lượt xem
                                  </span>
                                  <span>
                                    <ClockCircleOutlined /> {video.publishedText}
                                  </span>
                                </div>
                              </div>
                            }
                          />
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabPane>
        </Tabs>

        {/* Video Player */}
        <div className={styles.playerContainer}>
          {embedUrl ? (
            <>
              <iframe
                ref={iframeRef}
                className={styles.playerFrame}
                src={embedUrl}
                title="YouTube Video Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              {isPiPSupported && (
                <button 
                  className={styles.pipButton}
                  onClick={handlePictureInPicture}
                  title="Mini Player"
                >
                  <PictureOutlined /> Mini
                </button>
              )}
            </>
          ) : (
            <div className={styles.noVideo}>
              <EyeOutlined style={{ fontSize: '48px', color: '#999', marginBottom: '8px' }} />
              <div>Chưa có video nào được tải</div>
            </div>
          )}
        </div>

        {/* Controls */}
        {embedUrl && (
          <div className={styles.controls}>
            <Button 
              className={styles.controlButton}
              icon={isPiPSupported ? <PictureOutlined /> : <ExpandOutlined />}
              onClick={handlePictureInPicture}
              disabled={!isPiPSupported}
              style={{
                backgroundColor: '#212121',
                borderColor: '#333',
                color: '#fff',
                borderRadius: '18px',
                fontWeight: '500'
              }}
            >
              {isPiPSupported ? 'Picture-in-Picture' : 'Không hỗ trợ'}
            </Button>
            <Button 
              className={styles.controlButton}
              icon={<PauseCircleOutlined />}
              onClick={handleClearVideo}
              style={{
                backgroundColor: '#212121',
                borderColor: '#333',
                color: '#fff',
                borderRadius: '18px',
                fontWeight: '500'
              }}
            >
              Dừng & Xóa
            </Button>
          </div>
        )}

        {/* Status Message */}
        {status && (
          <div className={`${styles.status} ${styles[`status${status.type === 'success' ? 'Success' : status.type === 'error' ? 'Error' : 'Info'}`]}`}>
            {status.message}
          </div>
        )}

        {/* Features List */}
        <div className={styles.features}>
          <div className={styles.featuresTitle}>Tính năng</div>
          <div className={styles.featuresList}>
            <div className={styles.featureItem}>
              <SearchOutlined className={styles.featureIcon} />
              <span className={styles.featureText}>
                Tìm kiếm thông minh: API thực + fallback tìm trên YouTube
              </span>
            </div>
            <div className={styles.featureItem}>
              <SafetyCertificateOutlined className={styles.featureIcon} />
              <span className={styles.featureText}>
                Chặn quảng cáo nâng cao: youtube-nocookie + tham số experimental
              </span>
            </div>
            <div className={styles.featureItem}>
              <PictureOutlined className={styles.featureIcon} />
              <span className={styles.featureText}>
                Picture-in-Picture: Hỗ trợ iOS/iPad/Desktop với API native
              </span>
            </div>
            <div className={styles.featureItem}>
              <CheckCircleOutlined className={styles.featureIcon} />
              <span className={styles.featureText}>
                Không hiển thị video liên quan và branding tối thiểu
              </span>
            </div>
            <div className={styles.featureItem}>
              <EyeOutlined className={styles.featureIcon} />
              <span className={styles.featureText}>
                Giao diện Dark Theme giống YouTube với UX tối ưu
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayerPageMobile;